import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../SidebarInAdminPanel/Sidebar';
import { FaExclamationTriangle, FaClock, FaCheckCircle, FaArrowLeft } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { firestore } from '../../backend/firebaseConfig';
import SuccessDialog from '../DialogHandling/SuccessDialog';
import ErrorDialog from '../DialogHandling/ErrorDialog';
import LoadingPage from '../Loading/Loading';

const ComplaintDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [complaint, setComplaint] = useState(null);
  const [response, setResponse] = useState('');
  const [loading, setLoading] = useState(true);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const fetchComplaint = async () => {
      setLoading(true);
      try {
        const docRef = doc(firestore, "Complaints", id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setComplaint({ id: docSnap.id, ...docSnap.data() });
          setLoading(false);
        } else {
          setErrorMessage("No such complaint found.");
          setShowErrorDialog(true);
          setLoading(false);
        }
      } catch (error) {
        console.error("Error fetching complaint:", error);
        setErrorMessage("Error fetching complaint details.");
        setShowErrorDialog(true);
        setLoading(false);
      }
    };

    fetchComplaint();
  }, [id]);

  const handleResponseChange = (event) => {
    setResponse(event.target.value);
  };

  const handleSubmit = async () => {
    if (!response.trim()) {
      setErrorMessage("Please enter a valid response.");
      setShowErrorDialog(true);
      return;
    }

    try {
      const docRef = doc(firestore, "Complaints", complaint.id);
      await updateDoc(docRef, {
        response: response.trim(),
        status: "Resolved"
      });
      setShowSuccessDialog(true);
      setTimeout(() => navigate('/complaints'), 2000);
    } catch (error) {
      console.error("Error updating document:", error);
      setErrorMessage("Failed to submit response. Please try again.");
      setShowErrorDialog(true);
    }
  };

  const getStatusIcon = (status) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return <FaClock className="text-yellow-500" />;
      case 'resolved':
        return <FaCheckCircle className="text-green-500" />;
      default:
        return <FaExclamationTriangle className="text-red-500" />;
    }
  };

  if (loading) return <LoadingPage />;
  if (!complaint) return navigate('/complaints');

  return (
    <div className="min-h-screen bg-gradient-to-r from-[#0AD1C8] to-[#073235] flex">
      <Sidebar />
      <div className="container mx-auto px-4 py-8 w-full">
  
        {/* Back Button and Status */}
        <div className="flex justify-between items-center mb-6">
          <motion.button
            initial={{ x: -10, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            onClick={() => navigate('/complaints')}
            className="text-lg flex items-center text-white font-semibold transform transition-transform duration-300 hover:scale-105 hover:text-yellow-300"
          >
            <FaArrowLeft className="mr-2" />
            Back to Complaints
          </motion.button>
  
          <div className="flex items-center gap-2 bg-white px-4 py-1 rounded-full shadow-md w-fit">
  {getStatusIcon(complaint.status)}
  <span className="text-lg font-semibold capitalize text-green-600">{complaint.status}</span>
</div>

        </div>
  
        {/* Green Complaint Details Box */}
       {/* Complaint Details Box with Two Rows */}
<div className="bg-green-200 text-gray-800 rounded-xl shadow-md p-6 space-y-4">
  <h2 className="text-2xl font-bold">Complaint Details</h2>

  {/* First row */}
  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
    <p><strong>Complaint ID:</strong> {complaint.id}</p>
    <p><strong>User Email:</strong> {complaint.userEmail}</p>
    <p><strong>Category:</strong> {complaint.category}</p>
  </div>

  {/* Second row */}
  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
    <p><strong>Priority:</strong> {complaint.priority}</p>
    <p><strong>Date & Time:</strong> {new Date(complaint.timestamp).toLocaleString()}</p>
    <p><strong>Subject:</strong> {complaint.subject}</p>
  </div>

  {/* Description */}
  <div>
    <p><strong>Description:</strong> {complaint.description}</p>
  </div>
</div>

  
        {/* Textarea and Button */}
        <div className="mt-6">
          <h3 className="text-xl font-semibold text-white mb-3">Admin Response</h3>
          <textarea
            id="response"
            rows={4}
            className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300"
            value={response}
            onChange={handleResponseChange}
            placeholder="Type your response here..."
          ></textarea>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleSubmit}
            className="mt-4 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow-md transition"
          >
            Submit Response
          </motion.button>
        </div>
  
        {/* Dialogs */}
        {showSuccessDialog && (
          <SuccessDialog
            message="Response submitted successfully!"
            onClose={() => setShowSuccessDialog(false)}
            isOpen={showSuccessDialog}
          />
        )}
        {showErrorDialog && (
          <ErrorDialog
            message={errorMessage}
            onClose={() => setShowErrorDialog(false)}
            isOpen={showErrorDialog}
          />
        )}
      </div>
    </div>
  );
  
};

export default ComplaintDetailPage;